import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { QRCodeCanvas } from 'qrcode.react';
import { Stack, Typography } from '@mui/material';

import { AppDispatch, RootState } from 'redux/store';
import { downloadQR, ensureBasketQrCode } from 'redux/slices/qr/qrCodeSlice';
import CardWrapper from 'components/Common/CardWrapper';
import MenuList from 'components/Common/MenuList';
import MuiLoader from 'components/Common/Loader';
import { selectSelectedParishId } from 'redux/selectors/parishContext';
import { getParishGeneralFundBasket } from 'services/getBasketData';

/**
 * Parish-level QR.
 *
 * Every QR code targets a basket — there is no null-`basketId` code, because a donation row
 * requires a basket and an untargeted code would fail at payment, after printing. "Give to this
 * parish directly" is therefore a real basket: the per-parish system-owned General Fund.
 *
 * This page resolves that basket and runs the same basket-scoped ensure as every other QR
 * screen. No special case, no parish-wide lookup.
 */
const ViewQRCode = () => {
    const dispatch = useDispatch<AppDispatch>();
    const parishId = useSelector(selectSelectedParishId);
    const parishName = useSelector((state: RootState) => state.user.selectedParishName);
    const { activeQr, loading, inflightParishId, inflightBasketId } = useSelector((state: RootState) => state.qrCode);

    const [generalFundId, setGeneralFundId] = useState<string | null>(null);
    const [lookupDone, setLookupDone] = useState(false);

    // Resolved here rather than read off an already-loaded list: the basket list is fetched by
    // Home, so a direct visit or refresh on this route has none, and it is paginated by
    // `createdAt desc` — which sorts the General Fund last, making it the row most likely to be
    // missing from whatever page happens to be in the store.
    useEffect(() => {
        let cancelled = false;
        setGeneralFundId(null);
        setLookupDone(false);
        if (!parishId) {
            setLookupDone(true);
            return undefined;
        }
        getParishGeneralFundBasket(parishId)
            .then((basket) => {
                if (cancelled) return;
                setGeneralFundId(basket?.id ? String(basket.id) : null);
            })
            .catch(() => {
                // Render the empty state rather than a broken screen.
                if (!cancelled) setGeneralFundId(null);
            })
            .finally(() => {
                if (!cancelled) setLookupDone(true);
            });
        return () => {
            cancelled = true;
        };
    }, [parishId]);

    const loadQr = useCallback(() => {
        if (!parishId || !generalFundId) return;
        dispatch(
            ensureBasketQrCode({
                parishId,
                basketId: generalFundId,
                label: parishName || 'Parish QR',
                description: 'default',
            })
        );
    }, [dispatch, parishId, generalFundId, parishName]);

    useEffect(() => {
        loadQr();
    }, [loadQr]);

    // Same identity guard as the basket modal: parish AND basket must both match, or a code
    // ensured for another basket could paint here under the parish name.
    const displayQr =
        parishId &&
        generalFundId &&
        inflightParishId === parishId &&
        inflightBasketId === generalFundId &&
        activeQr?.basketId === generalFundId &&
        activeQr?.payloadUrl
            ? activeQr
            : null;

    const handleDownloadQR = () => {
        if (!displayQr?.payloadUrl) return;
        downloadQR('qrcode', displayQr.id || 'qrCode');
    };

    // A parish provisioned before its General Fund exists genuinely has none. This is a permanent
    // state, not a transitional one — never assume the basket is there.
    const noGeneralFund = lookupDone && !generalFundId;
    const busy = loading || !lookupDone;

    return (
        <CardWrapper>
            <MuiLoader absolute loading={busy} />
            <Stack width="100%" alignItems="flex-end">
                <MenuList
                    mt={4}
                    menuItems={[
                        {
                            item: 'Download',
                            onClick: handleDownloadQR,
                            disabled: !displayQr?.payloadUrl,
                        },
                        { item: 'Print', hide: true, onClick: () => undefined },
                    ]}
                />
            </Stack>
            <Stack alignItems="center" mb={5}>
                <Typography fontSize="35px" mb={3}>
                    {parishName || 'Parish'}
                </Typography>
                {displayQr?.payloadUrl ? (
                    <QRCodeCanvas id="qrcode" value={displayQr.payloadUrl} size={300} />
                ) : (
                    !busy && (
                        <Typography variant="body1" color="text.secondary" textAlign="center">
                            {noGeneralFund
                                ? 'This parish has no general fund yet, so there is no parish QR code. Open a basket to view its QR code.'
                                : 'No QR code available for this parish.'}
                        </Typography>
                    )
                )}
            </Stack>
        </CardWrapper>
    );
};

export default ViewQRCode;
